import styled from 'styled-components';

// ── Outer wrapper — fills remaining height between step bar and submit bar ────
export const Step2WrapperStyled = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

// ── Sticky ZIP header ─────────────────────────────────────────────────────────
export const ZipSectionStyled = styled.div`
  padding: 0 24px;
  border-bottom: 1px solid ${({ theme }) => theme?.colors?.borderLight ?? '#E5E7EB'};
  background: ${({ theme }) => theme?.colors?.white ?? '#ffffff'};
  flex-shrink: 0;
  display: flex;
  align-items: stretch;
  gap: 16px;
`;

export const ZipSectionHeaderStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 0;
  flex-shrink: 0;
`;

export const ZipSectionTitleStyled = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111827'};
`;

export const ZipDisclaimerStyled = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme?.colors?.textSecondary ?? '#6B7280'};
`;

export const ZipFilePillStyled = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme?.colors?.borderLight ?? '#E5E7EB'};
  background: ${({ theme }) => theme?.colors?.backgroundLight ?? '#F3F4F6'};
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111827'};
  max-width: 260px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ZipActionsStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  flex-shrink: 0;
`;

export const ZipErrorTextStyled = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme?.colors?.error ?? '#DC2626'};
`;

// ── Scrollable middle ─────────────────────────────────────────────────────────
export const ReviewScrollableStyled = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background: ${({ theme }) => theme?.colors?.backgroundLight ?? '#F3F4F6'};
`;

// ── Section block (Auto-mapped / Needs Action) ────────────────────────────────
export const SectionBlockStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const SectionCountBadgeStyled = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  background: ${({ $variant, theme }) =>
    $variant === 'success'
      ? (theme?.colors?.successBg ?? '#D1FAE5')
      : $variant === 'warning'
      ? (theme?.colors?.warningBg ?? '#FFFBEB')
      : (theme?.colors?.backgroundLight ?? '#F3F4F6')};
  color: ${({ $variant, theme }) =>
    $variant === 'success'
      ? (theme?.colors?.success ?? '#059669')
      : $variant === 'warning'
      ? (theme?.colors?.warningMessage ?? '#996A13')
      : (theme?.colors?.textSecondary ?? '#6B7280')};
`;

export const EmptyStateTextStyled = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme?.colors?.textSecondary ?? '#6B7280'};
`;

// ── Row card ──────────────────────────────────────────────────────────────────
export const ReviewRowCardStyled = styled.div`
  border-radius: 8px;
  background: ${({ theme }) => theme?.colors?.white ?? '#ffffff'};
  overflow: hidden;
  border: 1px solid ${({ $status, theme }) =>
    $status === 'resolved'
      ? (theme?.colors?.success ?? '#059669')
      : $status === 'pending'
      ? (theme?.colors?.warningBorder ?? '#FFB020')
      : (theme?.colors?.borderLight ?? '#E5E7EB')};
  box-shadow: ${({ $status }) =>
    $status === 'resolved'
      ? '0 0 0 2px rgba(5,150,105,0.08)'
      : $status === 'pending'
      ? '0 0 0 2px rgba(255,176,32,0.10)'
      : '0 1px 3px rgba(0,0,0,0.06)'};
  transition: border-color 0.2s, box-shadow 0.2s;
`;

export const RowCardHeaderStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: ${({ theme }) => theme?.colors?.backgroundLight ?? '#F9FAFB'};
  border-bottom: ${({ $collapsed, theme }) =>
    $collapsed ? 'none' : `1px solid ${theme?.colors?.borderLight ?? '#E5E7EB'}`};
`;

export const RowCardBodyStyled = styled.div`
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const RowNumberTextStyled = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme?.colors?.textSecondary ?? '#9CA3AF'};
  flex-shrink: 0;
`;

export const RowStatusBadgeStyled = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 10px;
  flex-shrink: 0;
  white-space: nowrap;
  background: ${({ $variant, theme }) =>
    $variant === 'success'
      ? (theme?.colors?.successBg ?? '#D1FAE5')
      : $variant === 'warning'
      ? (theme?.colors?.warningBg ?? '#FFFBEB')
      : $variant === 'error'
      ? (theme?.colors?.errorBg ?? '#FEF2F2')
      : (theme?.colors?.backgroundLight ?? '#F3F4F6')};
  color: ${({ $variant, theme }) =>
    $variant === 'success'
      ? (theme?.colors?.success ?? '#059669')
      : $variant === 'warning'
      ? (theme?.colors?.warningMessage ?? '#996A13')
      : $variant === 'error'
      ? (theme?.colors?.error ?? '#DC2626')
      : (theme?.colors?.textSecondary ?? '#6B7280')};
  border: 1px solid ${({ $variant, theme }) =>
    $variant === 'success'
      ? (theme?.colors?.success ?? '#059669') + '40'
      : $variant === 'warning'
      ? (theme?.colors?.warningBorder ?? '#FFB020') + '80'
      : $variant === 'error'
      ? (theme?.colors?.error ?? '#DC2626') + '40'
      : (theme?.colors?.borderLight ?? '#E5E7EB')};
`;

// ── Matched file chip (auto-mapped) ──────────────────────────────────────────
export const MatchedFileChipStyled = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme?.colors?.success ?? '#059669'}40;
  background: ${({ theme }) => theme?.colors?.successBg ?? '#D1FAE5'};
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme?.colors?.success ?? '#059669'};
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const MatchedToLabelStyled = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme?.colors?.textSecondary ?? '#6B7280'};
`;

export const NoFileTextStyled = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme?.colors?.textSecondary ?? '#6B7280'};
`;

// ── Candidate options ─────────────────────────────────────────────────────────
export const CandidateListStyled = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 8px;
  align-items: center;
  overflow: hidden;
`;

export const CandidateDividerStyled = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme?.colors?.textPrimary ?? '#6B7280'};
  font-weight: 400;
  flex-shrink: 0;
  user-select: none;
`;

export const UploadRefGroupStyled = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const CandidateOptionStyled = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px;
  border-radius: 6px;
  border: 1px solid ${({ $selected, theme }) =>
    $selected ? (theme?.colors?.primary ?? '#008080') : (theme?.colors?.borderDefault ?? '#D1D5DB')};
  background: ${({ $selected, theme }) =>
    $selected ? (theme?.colors?.primaryLight ?? '#E6F7F7') : (theme?.colors?.white ?? '#ffffff')};
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  flex-shrink: 0;
  height: 30px;
  box-sizing: border-box;

  &:hover {
    border-color: ${({ theme }) => theme?.colors?.primary ?? '#008080'};
  }
`;

export const CandidateFilenameStyled = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111827'};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SkipLabelStyled = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme?.colors?.textSecondary ?? '#9CA3AF'};
  font-style: italic;
`;

export const CandidateHintStyled = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme?.colors?.textSecondary ?? '#9CA3AF'};
  margin-bottom: 4px;
`;

// ── Tab bar ───────────────────────────────────────────────────────────────────
export const TabBarStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  background: transparent;
  flex-shrink: 0;
  padding: 0;
`;

export const TabButtonStyled = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
  height: 100%;
  border: none;
  border-bottom: 2px solid ${({ $active, theme }) =>
    $active ? (theme?.colors?.primary ?? '#008080') : 'transparent'};
  background: transparent;
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  color: ${({ $active, theme }) =>
    $active ? (theme?.colors?.primary ?? '#008080') : (theme?.colors?.textSecondary ?? '#6B7280')};
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
  outline: none;

  &:hover {
    color: ${({ theme }) => theme?.colors?.primary ?? '#008080'};
  }
`;

// ── Modal body text ───────────────────────────────────────────────────────────
export const ModalBodyTextStyled = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111827'};
  margin: 0;

  strong {
    font-weight: 600;
    color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111827'};
  }
  
  `;